// utils
import { getMessage } from 'util/locale';
import { getLocaleUnitSymbol } from 'util/unit';
import { convertForLocale, inferUnit, convert } from 'util/unitConversion';

// constants
import { UNIT_TYPES } from 'constants/unitTypes';
import { overlayLayers } from 'constants/imagery';


export const convertDisplayValues = ({legend, cropNominalWeight, localized = true}) => {
  const key = legend.labelKey.split('--')[1];
  const convertValueTo = localized ? 'scalarLocaleString' : 'scalar';
  let unit, unitType;
  switch(key) {
    case overlayLayers.ACTUAL_RATE:
      unit = UNIT_TYPES.seed_ac;
      unitType = 'seed-density';
      break;
    case overlayLayers.COMBINE_SPEED:
    case overlayLayers.PLANTER_SPEED:
      unit = UNIT_TYPES.mph;
      unitType = 'land-speed';
      break;
    case overlayLayers.YIELD:
      unit = UNIT_TYPES.bu_ac;
      unitType = 'area-density.yield';
      break;
    case overlayLayers.ELEVATION:
      unit = UNIT_TYPES.ft;
      unitType = 'elevation';
      break;
    default:
      // the other label keys are not based on units
      return legend.displayValues;
  }
  return legend.rawValues.map(value => {
    return convertForLocale({ quantity: value, unit, unitType, cropNominalWeight })[convertValueTo];
  });
};

export const getOverlayLegendTitle = (labelKey) => {
  const key = labelKey.split('--')[1];
  let unitType;
  switch(key) {
    case overlayLayers.ACTUAL_RATE:
      unitType = 'seed-density';
      break;
    case overlayLayers.COMBINE_SPEED:
    case overlayLayers.PLANTER_SPEED:
      unitType = 'land-speed';
      break;
    case overlayLayers.YIELD:
      unitType = 'area-density.yield';
      break;
    case overlayLayers.ELEVATION:
      unitType = 'elevation';
      break;
    default:
      // the other label keys are not based on units
      return getMessage(`${labelKey}--title`);
  }
  return getLocaleUnitSymbol(unitType);
};

/**
 * Get the unit key used for a map layer data
 * @param {String} mapType The type of data shown in the map layer
 * @returns {String | null} The alias of the correspondent unit key, if there is one
 */
export const getMapLayerUnitKey = (mapType) => {
  let unitType;
  switch(mapType) {
    case overlayLayers.ACTUAL_RATE:
      unitType = 'seed-density';
      break;
    case overlayLayers.COMBINE_SPEED:
    case overlayLayers.PLANTER_SPEED:
      unitType = 'land-speed';
      break;
    case overlayLayers.YIELD:
      unitType = 'area-density.yield';
      break;
    case overlayLayers.ELEVATION:
      unitType = 'elevation';
      break;
    default:
      // the layer does not have a unit key
      return null;
  }
  return inferUnit(unitType).unitKey;
};

/**
 * Get the values to update the legend
 * @param {Object} legend The legend data
 * @param {Number} legend.minValue Minimum value to set for the legend
 * @param {Number} legend.maxValue Maximum value to set for the legend
 * @param {String} unitKey The unit key used on the legend
 * @param {Number | null} cropNominalWeight The nominal weigth of the current crop, if there is one
 * @returns {{minValue: Number, maxValue: Number}} The min and the max values for the new legend
 */
export const getValuesForUpdate = ({ legend, unitKey, cropNominalWeight }) => {
  const valuesForUpdate = { minValue: legend.minValue, maxValue: legend.maxValue };

  if (unitKey === UNIT_TYPES.bag_ha) {
    for (const value in valuesForUpdate) {
      valuesForUpdate[value] = convert({
        from: { q: valuesForUpdate[value], u: unitKey },
        to: { u: UNIT_TYPES.mt_ha },
        cropNominalWeight,
        precision: 0
      }).q;
    }
  }  
  return valuesForUpdate;
};
